import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Loader2, Save, Sparkles } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";

const DEFAULTS = {
  overrun_policy: "free",
  overrun_amount_centavos: null,
  photo_requirement: "encouraged",
  delinquent_after_days: 60,
  abandoned_after_days: 120,
  reassignment_roles: ["owner", "chief_dentist"],
  dp_commission_on_reassignment: "stays_with_bonder",
  retention_visit_charge: "free",
  retention_visit_amount_centavos: null,
  retainer_charge_mode: "full_at_impression",
  retainer_price_removable_wire_centavos: 300000,
  retainer_price_clear_removable_centavos: 250000,
  retainer_price_fixed_wire_centavos: 0,
  retainer_price_other_centavos: 0,
  retainer_proc_rw_impression_id: "",
  retainer_proc_rw_installation_id: "",
  retainer_proc_cr_impression_id: "",
  retainer_proc_cr_installation_id: "",
  retainer_proc_fw_id: "",
  retainer_proc_other_impression_id: "",
  retainer_proc_other_installation_id: "",
  billing_setup_roles: ["clinic_admin", "receptionist"],
  billing_setup_overdue_after_days: 3,
  allow_full_payment_billing: true,
  pre_bonding_yellow_days: 7,
  pre_bonding_red_days: 30,
  default_refund_on_cancel: "none",
  reconciliation_approval_threshold_centavos: 500000,
  reconciliation_default_disposition: "collect_remaining",
  expected_adjustment_interval_days: 28,
  dental_os_go_live_date: "1970-01-01",
};

const ROLE_OPTIONS = [
  { value: "owner", label: "Owner" },
  { value: "chief_dentist", label: "Chief dentist" },
  { value: "admin", label: "Admin" },
];

const BILLING_ROLE_OPTIONS = [
  { value: "clinic_admin", label: "Clinic owner / admin" },
  { value: "receptionist", label: "Front desk / Admin staff" },
  { value: "doctor", label: "Doctors" },
];

const SYSTEM_CATEGORIES = [
  { value: "fixed_metal", label: "Fixed metal" },
  { value: "fixed_ceramic", label: "Fixed ceramic" },
  { value: "self_ligating", label: "Self-ligating" },
  { value: "aligner", label: "Aligner" },
  { value: "removable", label: "Removable" },
];

const WIRE_MATERIALS = ["NiTi", "CuNiTi", "Stainless Steel", "TMA", "Beta Titanium", "Other"];
const normalizeWireSetting = (wire) => ({
  ...wire,
  material: wire?.material === "SS" ? "Stainless Steel" : (wire?.material || "NiTi"),
});

export default function SettingsOrtho() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [systemsSaving, setSystemsSaving] = useState(false);
  const [wiresSaving, setWiresSaving] = useState(false);
  const [error, setError] = useState(null);
  const [v, setV] = useState(DEFAULTS);
  const [systems, setSystems] = useState([]);
  const [wires, setWires] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [systemRowErrors, setSystemRowErrors] = useState({});
  const [showRetainerAdvanced, setShowRetainerAdvanced] = useState(false);
  const [activeArea, setActiveArea] = useState("case");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [settingsRes, systemsRes, wiresRes, proceduresRes] = await Promise.all([
      supabase.rpc("get_ortho_settings"),
      supabase.rpc("list_ortho_systems", { p_include_inactive: true }),
      supabase.rpc("list_ortho_wire_options", { p_include_inactive: true }),
      supabase.rpc("list_procedures_for_settings", { p_include_archived: false }),
    ]);
    setLoading(false);
    if (settingsRes.error) {
      setError(settingsRes.error.message ?? "Could not load.");
      return;
    }
    setV({ ...DEFAULTS, ...(settingsRes.data ?? {}) });
    const secondaryErrors = [];
    if (systemsRes.error) secondaryErrors.push(`Could not load Ortho systems: ${systemsRes.error.message ?? "Unknown error."}`);
    else setSystems(Array.isArray(systemsRes.data) ? systemsRes.data : []);
    if (wiresRes.error) secondaryErrors.push(`Could not load Ortho wires: ${wiresRes.error.message ?? "Unknown error."}`);
    else setWires(Array.isArray(wiresRes.data) ? wiresRes.data.map(normalizeWireSetting) : []);
    if (proceduresRes.error) secondaryErrors.push(`Could not load procedures for Ortho bindings: ${proceduresRes.error.message ?? "Unknown error."}`);
    else setProcedures(Array.isArray(proceduresRes.data) ? proceduresRes.data : []);
    if (secondaryErrors.length) setError(secondaryErrors.join(" "));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const payload = useMemo(() => ({
    overrun_policy: v.overrun_policy,
    overrun_amount_centavos: v.overrun_policy === "per_visit_amount" ? v.overrun_amount_centavos : null,
    photo_requirement: v.photo_requirement,
    delinquent_after_days: Number(v.delinquent_after_days ?? 60),
    abandoned_after_days: Number(v.abandoned_after_days ?? 120),
    reassignment_roles: Array.isArray(v.reassignment_roles) ? v.reassignment_roles : DEFAULTS.reassignment_roles,
    dp_commission_on_reassignment: v.dp_commission_on_reassignment,
    retention_visit_charge: v.retention_visit_charge,
    retention_visit_amount_centavos: v.retention_visit_charge === "flat_amount" ? v.retention_visit_amount_centavos : null,
    retainer_charge_mode: v.retainer_charge_mode || "full_at_impression",
    retainer_price_removable_wire_centavos: Number(v.retainer_price_removable_wire_centavos ?? 0),
    retainer_price_clear_removable_centavos: Number(v.retainer_price_clear_removable_centavos ?? 0),
    retainer_price_fixed_wire_centavos: Number(v.retainer_price_fixed_wire_centavos ?? 0),
    retainer_price_other_centavos: Number(v.retainer_price_other_centavos ?? 0),
    retainer_proc_rw_impression_id: v.retainer_proc_rw_impression_id || null,
    retainer_proc_rw_installation_id: v.retainer_proc_rw_installation_id || null,
    retainer_proc_cr_impression_id: v.retainer_proc_cr_impression_id || null,
    retainer_proc_cr_installation_id: v.retainer_proc_cr_installation_id || null,
    retainer_proc_fw_id: v.retainer_proc_fw_id || null,
    retainer_proc_other_impression_id: v.retainer_proc_other_impression_id || null,
    retainer_proc_other_installation_id: v.retainer_proc_other_installation_id || null,
    billing_setup_roles: Array.isArray(v.billing_setup_roles) ? v.billing_setup_roles : DEFAULTS.billing_setup_roles,
    billing_setup_overdue_after_days: Number(v.billing_setup_overdue_after_days ?? 3),
    allow_full_payment_billing: !!v.allow_full_payment_billing,
    pre_bonding_yellow_days: Number(v.pre_bonding_yellow_days ?? 7),
    pre_bonding_red_days: Number(v.pre_bonding_red_days ?? 30),
    default_refund_on_cancel: v.default_refund_on_cancel || "none",
    reconciliation_approval_threshold_centavos: Number(v.reconciliation_approval_threshold_centavos ?? 500000),
    reconciliation_default_disposition: v.reconciliation_default_disposition || "collect_remaining",
    expected_adjustment_interval_days: Number(v.expected_adjustment_interval_days ?? 28),
    dental_os_go_live_date: v.dental_os_go_live_date || "1970-01-01",
  }), [v]);

  async function handleSave() {
    setSaving(true);
    setError(null);
    const { data, error: e } = await supabase.rpc("update_ortho_settings", { p_input: payload });
    setSaving(false);
    if (e) {
      setError(e.message ?? "Save failed.");
      return;
    }
    setV({ ...DEFAULTS, ...(data ?? {}) });
    toast.success("Ortho settings saved.");
  }

  function setField(k, val) {
    setV((prev) => ({ ...prev, [k]: val }));
  }

  function toggleRole(role) {
    setV((prev) => {
      const roles = new Set(Array.isArray(prev.reassignment_roles) ? prev.reassignment_roles : []);
      if (roles.has(role)) roles.delete(role);
      else roles.add(role);
      return { ...prev, reassignment_roles: Array.from(roles) };
    });
  }

  function toggleBillingRole(role) {
    setV((prev) => {
      const roles = new Set(Array.isArray(prev.billing_setup_roles) ? prev.billing_setup_roles : DEFAULTS.billing_setup_roles);
      if (roles.has(role)) roles.delete(role);
      else roles.add(role);
      return { ...prev, billing_setup_roles: Array.from(roles) };
    });
  }

  function updateSystem(index, patch) {
    setSystemRowErrors((prev) => {
      const key = systemRowKey(systems[index], index);
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setSystems((prev) => prev.map((system, i) => (i === index ? { ...system, ...patch } : system)));
  }

  function addSystem() {
    const nextOrder = Math.max(0, ...systems.map((system) => Number(system.display_order || 0))) + 10;
    setSystems((prev) => [
      ...prev,
      {
        id: "",
        category: "fixed_metal",
        display_name: "",
        default_price_centavos: null,
        is_active: true,
        display_order: nextOrder,
        bonding_procedure_id: "",
        adjustment_procedure_id: "",
        debonding_procedure_id: "",
      },
    ]);
  }

  const orthoProcedureOptions = useMemo(() => {
    const byType = { bonding: [], adjustment: [], debonding: [] };
    for (const procedure of procedures) {
      const type = procedure.ortho_visit_type;
      if (!byType[type]) continue;
      byType[type].push(procedure);
    }
    for (const type of Object.keys(byType)) {
      byType[type].sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
    }
    return byType;
  }, [procedures]);

  const procedureOptions = useMemo(
    () => [...procedures].sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""))),
    [procedures],
  );

  function moveSystem(index, direction) {
    setSystems((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next.map((system, i) => ({ ...system, display_order: (i + 1) * 10 }));
    });
  }

  async function saveSystems() {
    const validation = validateSystemsForSave(systems);
    setSystemRowErrors(validation.rowErrors);
    if (validation.messages.length) {
      setError(validation.messages.join(" "));
      toast.error("Some Ortho systems need procedure mappings before saving.");
      return;
    }

    setSystemsSaving(true);
    setError(null);
    try {
      const p_inputs = systems
        .filter((system) => String(system.display_name || "").trim())
        .map((system) => orthoSystemPayload(system));
      const { error: e } = await supabase.rpc("upsert_ortho_systems", { p_inputs });
      if (e) {
        const message = e.message ?? "Could not save Ortho systems.";
        const errorKey = systemErrorKeyFromMessage(systems, message);
        if (errorKey) setSystemRowErrors({ [errorKey]: message });
        throw new Error(message);
      }
      const { data, error: listError } = await supabase.rpc("list_ortho_systems", { p_include_inactive: true });
      if (listError) throw listError;
      setSystems(Array.isArray(data) ? data : []);
      setSystemRowErrors({});
      toast.success("Ortho systems saved.");
    } catch (e) {
      setError(e.message ?? "Could not save Ortho systems.");
      toast.error(e.message ?? "Could not save Ortho systems.");
    } finally {
      setSystemsSaving(false);
    }
  }

  function updateWire(index, patch) {
    setWires((prev) => prev.map((wire, i) => (i === index ? { ...wire, ...patch } : wire)));
  }

  function addWire() {
    const nextOrder = Math.max(0, ...wires.map((wire) => Number(wire.display_order || 0))) + 10;
    setWires((prev) => [
      ...prev,
      {
        id: "",
        display_name: "",
        wire_size: "",
        material: "NiTi",
        is_active: true,
        display_order: nextOrder,
      },
    ]);
  }

  function moveWire(index, direction) {
    setWires((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next.map((wire, i) => ({ ...wire, display_order: (i + 1) * 10 }));
    });
  }

  async function saveWires() {
    setWiresSaving(true);
    setError(null);
    try {
      for (const wire of wires) {
        if (!String(wire.wire_size || "").trim()) continue;
        const displayName = String(wire.display_name || "").trim() || `${wire.wire_size} ${wire.material}`;
        const { error: e } = await supabase.rpc("upsert_ortho_wire_option", {
          p_input: {
            id: wire.id || undefined,
            display_name: displayName,
            wire_size: String(wire.wire_size || "").trim(),
            material: wire.material || "NiTi",
            is_active: !!wire.is_active,
            display_order: Number(wire.display_order || 0),
          },
        });
        if (e) throw e;
      }
      const { data, error: listError } = await supabase.rpc("list_ortho_wire_options", { p_include_inactive: true });
      if (listError) throw listError;
      setWires(Array.isArray(data) ? data.map(normalizeWireSetting) : []);
      toast.success("Ortho wire options saved.");
    } catch (e) {
      setError(e.message ?? "Could not save Ortho wire options.");
    } finally {
      setWiresSaving(false);
    }
  }

  if (error && /permission denied/i.test(error)) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-8 text-center" data-testid="ortho-denied">
        <Sparkles className="w-8 h-8 mx-auto text-slate-300" />
        <p className="text-sm text-slate-600 mt-3">You need <code className="text-xs bg-slate-100 px-1 rounded">clinic.settings.manage</code>.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="settings-ortho">
      <div className="bg-white rounded-2xl shadow-sm px-5 py-3">
        <h1 className="text-lg font-semibold text-slate-900">Ortho setup</h1>
        <p className="text-xs text-slate-500">Set the clinic defaults used for new Ortho cases.</p>
      </div>

      {error && !/permission denied/i.test(error) ? (
        <div role="alert" className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{error}</div>
      ) : null}

      {loading ? (
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center text-sm text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading...
        </div>
      ) : (
        <>
        <div className="grid grid-cols-1 gap-2 rounded-2xl border border-slate-200 bg-white p-2 shadow-sm sm:grid-cols-3" aria-label="Ortho settings sections">
          {[
            { key: "case", label: "Case rules", desc: "Visits, photos, and inactivity" },
            { key: "treatment", label: "Systems & wires", desc: "Clinical treatment setup" },
            { key: "billing", label: "Billing & retention", desc: "Payments, reassignment, and retainers" },
          ].map((area) => (
            <button
              key={area.key}
              type="button"
              onClick={() => setActiveArea(area.key)}
              data-testid={`ortho-settings-area-${area.key}`}
              className={`min-h-14 rounded-xl px-4 py-2 text-left transition ${activeArea === area.key ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"}`}
            >
              <span className="block text-sm font-semibold">{area.label}</span>
              <span className={`mt-0.5 block text-xs ${activeArea === area.key ? "text-slate-300" : "text-slate-400"}`}>{area.desc}</span>
            </button>
          ))}
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-6">
          {activeArea === "case" ? (
          <>
          <section className="space-y-4">
            <SectionTitle title="Visit overruns" desc="What happens if a case needs more visits than the signed estimate." />
            <SelectField
              label="Overrun policy"
              value={v.overrun_policy}
              onChange={(value) => setField("overrun_policy", value)}
              options={[
                { value: "free", label: "Free extra visits" },
                { value: "per_visit_amount", label: "Charge a set amount" },
              ]}
              testid="ortho-overrun-policy"
            />
            {v.overrun_policy === "per_visit_amount" ? (
              <Pesos
                label="Overrun visit charge"
                desc="Used only after the estimated visit count is exceeded."
                centavos={v.overrun_amount_centavos}
                onChange={(n) => setField("overrun_amount_centavos", n)}
                testid="ortho-overrun-amount"
              />
            ) : null}
          </section>

          <section className="space-y-4 border-t border-slate-100 pt-5">
            <SectionTitle title="Case discipline" desc="Photo and inactivity rules used by the Ortho case workflow." />
            <SelectField
              label="Progress photos"
              value={v.photo_requirement}
              onChange={(value) => setField("photo_requirement", value)}
              options={[
                { value: "required", label: "Required" },
                { value: "encouraged", label: "Encouraged" },
                { value: "optional", label: "Optional" },
              ]}
              testid="ortho-photo-requirement"
            />
            <Field
              label="Delinquent after days"
              desc="Days with no visit before the case is flagged delinquent."
              min={0}
              max={365}
              val={v.delinquent_after_days}
              onChange={(n) => setField("delinquent_after_days", n)}
              testid="ortho-delinquent-days"
            />
            <Field
              label="Abandoned after days"
              desc="Days with no visit before the case can be marked abandoned."
              min={0}
              max={730}
              val={v.abandoned_after_days}
              onChange={(n) => setField("abandoned_after_days", n)}
              testid="ortho-abandoned-days"
            />
            <Field
              label="Adjustment interval (days)"
              desc="Default cadence used by Ortho Center overdue and at-risk rules."
              min={7}
              max={90}
              val={v.expected_adjustment_interval_days}
              onChange={(n) => setField("expected_adjustment_interval_days", n)}
              testid="ortho-adjustment-interval-days"
            />
            <label className="block">
              <div className="text-xs font-medium text-slate-700">Dental OS go-live date</div>
              <div className="text-[11px] text-slate-500 mb-1">Payments before this date are historical context, not Dental OS collections.</div>
              <input
                type="date"
                value={v.dental_os_go_live_date || "1970-01-01"}
                onChange={(event) => setField("dental_os_go_live_date", event.target.value)}
                data-testid="ortho-dental-os-go-live-date"
                className="h-9 rounded-lg border border-slate-200 px-3 text-sm text-slate-800 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
            </label>
          </section>

          </>
          ) : null}

          {activeArea === "treatment" ? (
          <>

          <section className="space-y-4 border-t border-slate-100 pt-5">
            <SectionHeaderWithAction
              title="Systems"
              desc="Clinic-owned Ortho systems. Each active system must declare its bonding, adjustment, and debonding procedures."
              action={(
                <button
                  type="button"
                  onClick={saveSystems}
                  disabled={systemsSaving}
                  className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                >
                  {systemsSaving ? "Saving systems..." : "Save systems"}
                </button>
              )}
            />
            <div className="space-y-2">
              {systems.map((system, index) => (
                <div key={system.id || `new-${index}`} className="rounded-xl border border-slate-200 p-3">
                  <div className="grid grid-cols-1 gap-2 lg:grid-cols-[150px_1fr_160px_90px_100px] lg:items-center">
                    <select
                      value={system.category}
                      onChange={(e) => updateSystem(index, { category: e.target.value })}
                      className="h-9 rounded-lg border border-slate-200 px-2 text-sm"
                    >
                      {SYSTEM_CATEGORIES.map((category) => (
                        <option key={category.value} value={category.value}>{category.label}</option>
                      ))}
                    </select>
                    <input
                      value={system.display_name || ""}
                      onChange={(e) => updateSystem(index, { display_name: e.target.value })}
                      placeholder="System name"
                      className="h-9 rounded-lg border border-slate-200 px-3 text-sm"
                    />
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm text-slate-500">₱</span>
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        value={system.default_price_centavos != null ? system.default_price_centavos / 100 : ""}
                        onChange={(e) => updateSystem(index, { default_price_centavos: e.target.value === "" ? null : Math.max(0, Math.round(Number(e.target.value) * 100)) })}
                        placeholder="Default"
                        className="h-9 w-full rounded-lg border border-slate-200 px-3 text-sm"
                      />
                    </div>
                    <label className="inline-flex items-center gap-2 text-sm text-slate-600">
                      <input
                        type="checkbox"
                        checked={!!system.is_active}
                        onChange={(e) => updateSystem(index, { is_active: e.target.checked })}
                        className="h-4 w-4 rounded border-slate-300 text-teal-600"
                      />
                      Active
                    </label>
                    <div className="flex gap-1">
                      <button type="button" onClick={() => moveSystem(index, -1)} className="h-8 rounded-lg border border-slate-200 px-2 text-xs font-semibold text-slate-600">Up</button>
                      <button type="button" onClick={() => moveSystem(index, 1)} className="h-8 rounded-lg border border-slate-200 px-2 text-xs font-semibold text-slate-600">Down</button>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-1 gap-2 lg:grid-cols-3">
                    <ProcedureBindingSelect
                      label="Bonding"
                      value={system.bonding_procedure_id || ""}
                      options={orthoProcedureOptions.bonding}
                      onChange={(bonding_procedure_id) => updateSystem(index, { bonding_procedure_id })}
                    />
                    <ProcedureBindingSelect
                      label="Adjustment"
                      value={system.adjustment_procedure_id || ""}
                      options={orthoProcedureOptions.adjustment}
                      onChange={(adjustment_procedure_id) => updateSystem(index, { adjustment_procedure_id })}
                    />
                    <ProcedureBindingSelect
                      label="Debonding"
                      value={system.debonding_procedure_id || ""}
                      options={orthoProcedureOptions.debonding}
                      onChange={(debonding_procedure_id) => updateSystem(index, { debonding_procedure_id })}
                    />
                  </div>
                  {systemRowErrors[systemRowKey(system, index)] ? (
                    <div role="alert" className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                      {systemRowErrors[systemRowKey(system, index)]}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
            <div className="flex flex-wrap justify-between gap-2">
              <button type="button" onClick={addSystem} className="rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">
                + Add system
              </button>
              <button type="button" onClick={saveSystems} disabled={systemsSaving} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                {systemsSaving ? "Saving systems..." : "Save systems"}
              </button>
            </div>
          </section>

          <section className="space-y-4 border-t border-slate-100 pt-5">
            <SectionTitle title="Wire options" desc="Clinic-owned wire list used by bonding and adjustment visits." />
            <div className="space-y-2">
              {wires.map((wire, index) => (
                <div key={wire.id || `new-wire-${index}`} className="grid grid-cols-1 gap-2 rounded-xl border border-slate-200 p-3 lg:grid-cols-[130px_120px_1fr_90px_100px] lg:items-center">
                  <input
                    value={wire.wire_size || ""}
                    onChange={(e) => updateWire(index, { wire_size: e.target.value, display_name: `${e.target.value} ${wire.material || "NiTi"}`.trim() })}
                    placeholder="0.014"
                    className="h-9 rounded-lg border border-slate-200 px-3 text-sm"
                  />
                  <select
                    value={wire.material || "NiTi"}
                    onChange={(e) => updateWire(index, { material: e.target.value, display_name: `${wire.wire_size || ""} ${e.target.value}`.trim() })}
                    className="h-9 rounded-lg border border-slate-200 px-2 text-sm"
                  >
                    {WIRE_MATERIALS.map((material) => (
                      <option key={material} value={material}>{material}</option>
                    ))}
                  </select>
                  <input
                    value={wire.display_name || ""}
                    onChange={(e) => updateWire(index, { display_name: e.target.value })}
                    placeholder="Display name"
                    className="h-9 rounded-lg border border-slate-200 px-3 text-sm"
                  />
                  <label className="inline-flex items-center gap-2 text-sm text-slate-600">
                    <input
                      type="checkbox"
                      checked={!!wire.is_active}
                      onChange={(e) => updateWire(index, { is_active: e.target.checked })}
                      className="h-4 w-4 rounded border-slate-300 text-teal-600"
                    />
                    Active
                  </label>
                  <div className="flex gap-1">
                    <button type="button" onClick={() => moveWire(index, -1)} className="h-8 rounded-lg border border-slate-200 px-2 text-xs font-semibold text-slate-600">Up</button>
                    <button type="button" onClick={() => moveWire(index, 1)} className="h-8 rounded-lg border border-slate-200 px-2 text-xs font-semibold text-slate-600">Down</button>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap justify-between gap-2">
              <button type="button" onClick={addWire} className="rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">
                + Add wire
              </button>
              <button type="button" onClick={saveWires} disabled={wiresSaving} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                {wiresSaving ? "Saving wires..." : "Save wires"}
              </button>
            </div>
          </section>

          </>
          ) : null}

          {activeArea === "billing" ? (
          <>

          <section className="space-y-4 border-t border-slate-100 pt-5">
            <SectionTitle title="Billing options" desc="Controls which payment structures are available during Ortho billing setup." />
            <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm">
              <input
                type="checkbox"
                checked={!!v.allow_full_payment_billing}
                onChange={(e) => setField("allow_full_payment_billing", e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                data-testid="ortho-allow-full-payment"
              />
              <span>
                <span className="block font-semibold text-slate-800">Allow full payment option</span>
                <span className="mt-0.5 block text-xs text-slate-500">When enabled, billing setup can choose one full payment instead of a number-of-payments plan.</span>
              </span>
            </label>
          </section>

          <section className="space-y-4 border-t border-slate-100 pt-5">
            <SectionTitle title="Pre-bonding" desc="How long active cases can wait for installation before Ortho Center flags them." />
            <Field
              label="Stale yellow flag days"
              desc="Cases waiting this long after contract generation show Awaiting bonding."
              min={0}
              max={365}
              val={v.pre_bonding_yellow_days}
              onChange={(n) => setField("pre_bonding_yellow_days", n)}
              testid="ortho-prebonding-yellow-days"
            />
            <Field
              label="Stale red flag days"
              desc="Cases waiting this long after contract generation show Stale - follow up or cancel."
              min={0}
              max={365}
              val={v.pre_bonding_red_days}
              onChange={(n) => setField("pre_bonding_red_days", n)}
              testid="ortho-prebonding-red-days"
            />
            <SelectField
              label="Default refund on cancel"
              value={v.default_refund_on_cancel}
              onChange={(value) => setField("default_refund_on_cancel", value)}
              options={[
                { value: "none", label: "None" },
                { value: "full", label: "Full" },
                { value: "partial", label: "Partial" },
              ]}
              testid="ortho-default-refund-on-cancel"
            />
          </section>

          <section className="space-y-4 border-t border-slate-100 pt-5">
            <SectionTitle title="Reconciliation" desc="Rules used when an Ortho case is closed, paused, abandoned, or moved into retention." />
            <Pesos
              label="Owner approval threshold"
              desc="Write-offs or discounts above this amount require owner/admin approval."
              centavos={v.reconciliation_approval_threshold_centavos}
              onChange={(n) => setField("reconciliation_approval_threshold_centavos", n)}
              testid="ortho-reconciliation-threshold"
            />
            <SelectField
              label="Default close disposition"
              value={v.reconciliation_default_disposition}
              onChange={(value) => setField("reconciliation_default_disposition", value)}
              options={[
                { value: "collect_remaining", label: "Collect remaining" },
                { value: "mark_complete", label: "Mark complete" },
                { value: "always_ask", label: "Always ask" },
              ]}
              testid="ortho-reconciliation-default"
            />
          </section>

          <section className="space-y-4 border-t border-slate-100 pt-5">
            <SectionTitle title="Permissions" desc="Controls who can complete Ortho billing setup before bonding can be booked." />
            <div>
              <label className="text-sm font-medium text-slate-800">Who can complete billing setup?</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {BILLING_ROLE_OPTIONS.map((role) => {
                  const checked = Array.isArray(v.billing_setup_roles) && v.billing_setup_roles.includes(role.value);
                  return (
                    <label
                      key={role.value}
                      className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${checked ? "border-teal-300 bg-teal-50 text-teal-800" : "border-slate-200 text-slate-600"}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleBillingRole(role.value)}
                        className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                        data-testid={`ortho-billing-role-${role.value}`}
                      />
                      {role.label}
                    </label>
                  );
                })}
              </div>
            </div>
            <Field
              label="Pending billing overdue after days"
              desc="Cases waiting for front desk billing setup show as overdue after this many days."
              min={0}
              max={30}
              val={v.billing_setup_overdue_after_days}
              onChange={(n) => setField("billing_setup_overdue_after_days", n)}
              testid="ortho-billing-overdue-days"
            />
          </section>

          <section className="space-y-4 border-t border-slate-100 pt-5">
            <SectionTitle title="Reassignment" desc="Who can move an active Ortho case to another doctor, and how down-payment credit behaves." />
            <div>
              <label className="text-sm font-medium text-slate-800">Allowed reassignment roles</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {ROLE_OPTIONS.map((role) => {
                  const checked = Array.isArray(v.reassignment_roles) && v.reassignment_roles.includes(role.value);
                  return (
                    <label
                      key={role.value}
                      className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${checked ? "border-teal-300 bg-teal-50 text-teal-800" : "border-slate-200 text-slate-600"}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleRole(role.value)}
                        className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                        data-testid={`ortho-role-${role.value}`}
                      />
                      {role.label}
                    </label>
                  );
                })}
              </div>
            </div>
            <SelectField
              label="Down-payment commission on reassignment"
              value={v.dp_commission_on_reassignment}
              onChange={(value) => setField("dp_commission_on_reassignment", value)}
              options={[
                { value: "stays_with_bonder", label: "Stays with bonding doctor" },
                { value: "pro_rated", label: "Pro-rated" },
                { value: "transfers", label: "Transfers to new doctor" },
              ]}
              testid="ortho-dp-commission"
            />
          </section>

          <section className="space-y-4 border-t border-slate-100 pt-5">
            <SectionTitle title="Retainers" desc="Prices and procedure mappings are snapshotted when the doctor configures a post-debonding retainer." />
            <SelectField
              label="Retainer charge mode"
              value={v.retainer_charge_mode}
              onChange={(value) => setField("retainer_charge_mode", value)}
              options={[
                { value: "full_at_impression", label: "Full at impression" },
                { value: "split", label: "Split impression / installation" },
              ]}
              testid="ortho-retainer-charge-mode"
            />
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-4">
              <Pesos
                label="Removable Wire"
                desc="Per arch."
                centavos={v.retainer_price_removable_wire_centavos}
                onChange={(n) => setField("retainer_price_removable_wire_centavos", n ?? 0)}
                testid="ortho-retainer-price-removable-wire"
              />
              <Pesos
                label="Clear Removable"
                desc="Per arch."
                centavos={v.retainer_price_clear_removable_centavos}
                onChange={(n) => setField("retainer_price_clear_removable_centavos", n ?? 0)}
                testid="ortho-retainer-price-clear-removable"
              />
              <Pesos
                label="Fixed Wire"
                desc="Per arch, charged at debonding."
                centavos={v.retainer_price_fixed_wire_centavos}
                onChange={(n) => setField("retainer_price_fixed_wire_centavos", n ?? 0)}
                testid="ortho-retainer-price-fixed-wire"
              />
              <Pesos
                label="Other"
                desc="Fallback per arch."
                centavos={v.retainer_price_other_centavos}
                onChange={(n) => setField("retainer_price_other_centavos", n ?? 0)}
                testid="ortho-retainer-price-other"
              />
            </div>

            <div className="rounded-xl border border-slate-200 bg-white">
              <button
                type="button"
                onClick={() => setShowRetainerAdvanced((prev) => !prev)}
                className="flex w-full items-center justify-between px-4 py-3 text-left"
              >
                <span>
                  <span className="block text-sm font-semibold text-slate-900">Retainer procedure mapping</span>
                  <span className="block text-xs text-slate-500">Advanced. Default mappings work for most clinics.</span>
                </span>
                {showRetainerAdvanced ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
              </button>
              {showRetainerAdvanced ? (
                <div className="grid grid-cols-1 gap-3 border-t border-slate-100 p-4 lg:grid-cols-2">
                  <ProcedureBindingSelect
                    label="Removable Wire impression"
                    value={v.retainer_proc_rw_impression_id}
                    options={procedureOptions}
                    onChange={(value) => setField("retainer_proc_rw_impression_id", value)}
                  />
                  <ProcedureBindingSelect
                    label="Removable Wire installation"
                    value={v.retainer_proc_rw_installation_id}
                    options={procedureOptions}
                    onChange={(value) => setField("retainer_proc_rw_installation_id", value)}
                  />
                  <ProcedureBindingSelect
                    label="Clear Removable impression"
                    value={v.retainer_proc_cr_impression_id}
                    options={procedureOptions}
                    onChange={(value) => setField("retainer_proc_cr_impression_id", value)}
                  />
                  <ProcedureBindingSelect
                    label="Clear Removable installation"
                    value={v.retainer_proc_cr_installation_id}
                    options={procedureOptions}
                    onChange={(value) => setField("retainer_proc_cr_installation_id", value)}
                  />
                  <ProcedureBindingSelect
                    label="Fixed Wire"
                    value={v.retainer_proc_fw_id}
                    options={procedureOptions}
                    onChange={(value) => setField("retainer_proc_fw_id", value)}
                  />
                  <ProcedureBindingSelect
                    label="Other impression"
                    value={v.retainer_proc_other_impression_id}
                    options={procedureOptions}
                    onChange={(value) => setField("retainer_proc_other_impression_id", value)}
                  />
                  <ProcedureBindingSelect
                    label="Other installation"
                    value={v.retainer_proc_other_installation_id}
                    options={procedureOptions}
                    onChange={(value) => setField("retainer_proc_other_installation_id", value)}
                  />
                  <p className="text-xs leading-5 text-slate-500 lg:col-span-2">
                    These map retainer types to catalog procedures used at billing. Override only if your clinic uses custom retainer procedure codes.
                  </p>
                </div>
              ) : null}
            </div>
          </section>

          <section className="space-y-4 border-t border-slate-100 pt-5">
            <SectionTitle title="Retention visits" desc="Legacy policy for existing retention visit records." />
            <SelectField
              label="Retention visit charge"
              value={v.retention_visit_charge}
              onChange={(value) => setField("retention_visit_charge", value)}
              options={[
                { value: "free", label: "Free" },
                { value: "flat_amount", label: "Flat amount" },
              ]}
              testid="ortho-retention-charge"
            />
            {v.retention_visit_charge === "flat_amount" ? (
              <Pesos
                label="Retention visit amount"
                desc="Applied only to retention visits when flat amount is selected."
                centavos={v.retention_visit_amount_centavos}
                onChange={(n) => setField("retention_visit_amount_centavos", n)}
                testid="ortho-retention-amount"
              />
            ) : null}
          </section>
          </>
          ) : null}
        </div>
        </>
      )}

      {activeArea !== "treatment" ? <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || loading}
          data-testid="ortho-save"
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-lg disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save settings
        </button>
      </div> : null}
    </div>
  );
}

function SectionTitle({ title, desc }) {
  return (
    <div>
      <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
      {desc ? <p className="text-xs text-slate-500 mt-0.5">{desc}</p> : null}
    </div>
  );
}

function SectionHeaderWithAction({ title, desc, action }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <SectionTitle title={title} desc={desc} />
      <div className="shrink-0">{action}</div>
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-sm font-medium text-slate-800">{label}</label>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1 w-full max-w-sm px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </div>
  );
}

function Field({ label, desc, min, max, val, onChange, testid }) {
  return (
    <div>
      <label className="text-sm font-medium text-slate-800">{label}</label>
      <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
      <input
        type="number"
        min={min}
        max={max}
        value={val ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? null : Math.max(min, Math.min(max, Number(e.target.value))))}
        data-testid={testid}
        className="mt-1 w-32 px-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none"
      />
    </div>
  );
}

function Pesos({ label, desc, centavos, onChange, testid }) {
  const pesos = centavos != null ? (centavos / 100).toFixed(2) : "";
  return (
    <div>
      <label className="text-sm font-medium text-slate-800">{label}</label>
      <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
      <div className="mt-1 inline-flex items-center gap-1.5">
        <span className="text-sm text-slate-500">₱</span>
        <input
          type="number"
          min={0}
          step="0.01"
          value={pesos}
          onChange={(e) => {
            const next = e.target.value;
            if (next === "") onChange(null);
            else onChange(Math.max(0, Math.round(Number(next) * 100)));
          }}
          data-testid={testid}
          className="w-40 px-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none"
        />
      </div>
    </div>
  );
}

function ProcedureBindingSelect({ label, value, options, onChange }) {
  const placeholder = `Select ${label.toLowerCase()} procedure`;
  return (
    <label className="block">
      <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">{label} procedure</span>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 h-9 w-full rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-700"
      >
        <option value="" label={placeholder} />
        {options.map((procedure) => (
          <option key={procedure.id} value={procedure.id} label={procedureOptionLabel(procedure)} />
        ))}
      </select>
    </label>
  );
}

function systemRowKey(system, index) {
  return system?.id || `new-${index}`;
}

function systemRowName(system, index) {
  return String(system?.display_name || "").trim() || `System ${index + 1}`;
}

function validateSystemsForSave(systems) {
  const rowErrors = {};
  const messages = [];

  systems.forEach((system, index) => {
    const name = systemRowName(system, index);
    const missing = [];
    if (!String(system.display_name || "").trim()) missing.push("System name");
    if (system.is_active) {
      if (!system.bonding_procedure_id) missing.push("Bonding procedure");
      if (!system.adjustment_procedure_id) missing.push("Adjustment procedure");
      if (!system.debonding_procedure_id) missing.push("Debonding procedure");
    }
    if (!missing.length) return;

    const message = `${name}: ${missing.join(", ")} required before saving.`;
    rowErrors[systemRowKey(system, index)] = message;
    messages.push(message);
  });

  return { rowErrors, messages };
}

function orthoSystemPayload(system) {
  return {
    id: system.id || undefined,
    category: system.category,
    display_name: String(system.display_name || "").trim(),
    default_price_centavos: system.default_price_centavos,
    is_active: !!system.is_active,
    display_order: Number(system.display_order || 0),
    bonding_procedure_id: system.bonding_procedure_id || null,
    adjustment_procedure_id: system.adjustment_procedure_id || null,
    debonding_procedure_id: system.debonding_procedure_id || null,
  };
}

function systemErrorKeyFromMessage(systems, message) {
  const text = String(message || "");
  for (let index = 0; index < systems.length; index += 1) {
    const name = systemRowName(systems[index], index);
    if (text.includes(`"${name}"`) || text.includes(name)) {
      return systemRowKey(systems[index], index);
    }
  }
  return null;
}

function procedureOptionLabel(procedure) {
  return `${procedure.name}${procedure.code ? ` (${procedure.code})` : ""}`;
}
